import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { setCurrentCategory } from '../store/slices/categoriesSlice';
import { setHistory } from "../store/slices/coloringSlice";
import { Image as ImageType } from '../types';
import { useImageUsage } from '../hooks/useImageUsage';
import { ImageStatus } from '../utils/ImageUsageManager';
import { trackPageView, trackUserInteraction } from '../utils/analytics';
import {Theme} from "../config/theme";

// Add highlight animation
const highlightAnimation = keyframes`
  0% { box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); }
  50% { box-shadow: 0 0 15px 5px rgba(0, 150, 136, 0.5); }
  100% { box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); }
`;

const CategoryContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Content = styled.div`
  flex: 1;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  padding: 0.5rem 1rem;
  margin-right: 1rem;
  background: #f0f0f0;
  border-radius: 8px;
  font-size: 1rem;
`;

const Title = styled.h1`
  color: ${Theme.colors.main};
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
  padding: 1rem;
`;

const ImageCard = styled.div<{ highlighted?: boolean }>`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s;
  animation: ${props => props.highlighted ? highlightAnimation : 'none'} 1.5s ease-in-out;

  &:hover {
    transform: translateY(-4px);
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 2/3;
  overflow: hidden;
`;

const ImageElement = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  top: 0;
  left: 0;
`;

const UsedBadge = styled.div<{ isCompleted?: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${props => props.isCompleted ? '#4CAF50' : 'rgba(0, 0, 0, 0.6)'};
  color: white;
  padding: 4px 8px;
  font-size: 0.8rem;
  text-align: center;
`;

const Footer = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  color: ${Theme.colors.footer.text};
`;

const FooterLink = styled.a`
  color: ${Theme.colors.footer.text};
  text-decoration: none;
  font-size: 0.9rem;
  
  &:hover {
    color: ${Theme.colors.footer.textHoover};
    text-decoration: underline;
  }
`;

interface EnrichedImage extends ImageType {
  isUsed?: boolean;
  lastUsedTimestamp?: number;
  status?: ImageStatus;
}

const Category: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const categories = useSelector((state: RootState) => state.categories.categories);
  const currentCategory = useSelector((state: RootState) => state.categories.currentCategory);
  
  // Create refs for image cards
  const imageRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // Keep track of the last image we scrolled to
  const lastScrolledToImageId = useRef<string | null>(null);
  
  // Use our hook to get images with usage data
  const { images: sortedImages, isLoading: isLoadingUsageData } = useImageUsage(
    categoryId,
    currentCategory?.images || []
  );

  // Add state to track which image to highlight
  const [highlightedImageId, setHighlightedImageId] = useState<string | null>(null);

  // Track page view when component mounts or categoryId changes
  useEffect(() => {
    if (categoryId && currentCategory) {
      trackPageView('Category', {
        category_id: categoryId,
        category_name: currentCategory.name,
        total_images: currentCategory.images?.length || 0,
        route_path: `/category/${categoryId}`
      });
    }
  }, [categoryId, currentCategory]);

  useEffect(() => {
    const category = categories.find(cat => cat.id === categoryId);
    if (category) {
      dispatch(setCurrentCategory(category));
      dispatch(setHistory({history: [], currentStep: 0}));
    }
  }, [categoryId, categories, dispatch]);
  
  // Function to scroll to the specified image
  const scrollToImage = useCallback((imageId: string) => {
    // Avoid scrolling to the same image multiple times
    if (imageId === lastScrolledToImageId.current) {
      return;
    }

    if (imageRefs.current[imageId]) {
      // Scroll to the image
      imageRefs.current[imageId]?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      
      // Highlight the image briefly
      setHighlightedImageId(imageId);
      setTimeout(() => {
        setHighlightedImageId(null);
      }, 1500); // Match animation duration
      
      // Update the last scrolled image
      lastScrolledToImageId.current = imageId;
    }
  }, []);
  
  // When returning from a coloring page, scroll to the most recently used image
  useEffect(() => {
    // Check if we're coming back from a coloring page
    const isReturningFromColoring = location.state?.from === 'coloring';
    const lastUsedImageId = location.state?.imageId;
    
    if (isReturningFromColoring && lastUsedImageId && !isLoadingUsageData) {
      // Use a small timeout to ensure the DOM is fully rendered
      const timer = setTimeout(() => {
        scrollToImage(lastUsedImageId);
      }, 100);
      
      return () => clearTimeout(timer);
    } else if (!isLoadingUsageData && sortedImages.length > 0 && !lastScrolledToImageId.current) {
      // Only scroll to the first image if we haven't scrolled anywhere yet
      const timer = setTimeout(() => {
        scrollToImage(sortedImages[0].id);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [location.state, scrollToImage, isLoadingUsageData, sortedImages]);

  const handleImageClick = (imageId: string) => {
    trackUserInteraction('image_click', {
      category_id: categoryId,
      image_id: imageId,
      from_page: 'category'
    });
    navigate(`/coloring/${categoryId}/${imageId}`);
  };

  if (!currentCategory || isLoadingUsageData) {
    return <div>Loading...</div>;
  }

  return (
    <CategoryContainer>
      <Content>
        <Header>
          <BackButton onClick={() => navigate('/')}>← Back</BackButton>
          <Title>{currentCategory.name}</Title>
        </Header>
        <ImageGrid>
          {sortedImages.map((image: EnrichedImage, index) => (
            <ImageCard 
              key={image.id} 
              onClick={() => handleImageClick(image.id)}
              ref={el => imageRefs.current[image.id] = el}
              highlighted={image.id === highlightedImageId}
            >
              <ImageWrapper>
                <ImageElement src={`/images/${image.path}`} alt={`Coloring image ${index + 1}`} />
                {image.isUsed && (
                  <UsedBadge isCompleted={image.status === ImageStatus.COMPLETED}>
                    {image.status === ImageStatus.COMPLETED ? 'Completed' : 'In Progress'}
                  </UsedBadge>
                )}
              </ImageWrapper>
            </ImageCard>
          ))}
        </ImageGrid>
      </Content>
      <Footer>
        <FooterLink href="/privacy-policy">Privacy Policy</FooterLink>
      </Footer>
    </CategoryContainer>
  );
};

export default Category; 